import { useMemo, useState } from "react";
import {
    Area,
    CartesianGrid,
    ComposedChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartLine, faArrowTrendUp, faArrowTrendDown } from "@fortawesome/free-solid-svg-icons";


/* ----------------------------- Types ------------------------------------- */

export type RangeKey = "30d" | "3m" | "6m" | "12m";

/** Chart-ready data derived from the API's `questionLibraryGrowth` block. */
export interface QuestionGrowthData {
    series: { label: string; added: number; verified: number }[];
    totalAdded: number;
    totalVerified: number;
    verificationRate: number; // 0..100
}

/** Internal datum used by the chart (adds derived verification rate). */
interface ChartDatum {
    label: string;
    added: number;
    verified: number;
    rate: number; // verified / added, 0..1
}

interface QuestionGrowthChartProps {
    /** Chart-ready growth data for the currently selected range. */
    data: QuestionGrowthData;
    loading: boolean;
    range: RangeKey;
    onRangeChange: (range: RangeKey) => void;
}

/* --------------------------- Configuration ------------------------------- */

const COLOR_ADDED = "#5B3DF5"; // primary violet
const COLOR_VERIFIED = "#10B981"; // emerald

const RANGES: { key: RangeKey; label: string }[] = [
    { key: "30d", label: "Last 30 Days" },
    { key: "3m", label: "Last 3 Months" },
    { key: "6m", label: "Last 6 Months" },
    { key: "12m", label: "Last 12 Months" },
];

/* ----------------------- API → chart mapping ----------------------------- */

/** Builds the X-axis label for a monthly data point (supports daily points too). */
function pointLabel(point: any): string {
    if (point?.label) return String(point.label);
    if (point?.day) return `${point.day} ${point.month ?? ""}`.trim();
    return String(point?.month ?? "");
}

/** Maps the raw `questionLibraryGrowth` API block into chart-ready data. */
export function mapGrowthResponse(growth?: any): QuestionGrowthData {
    const monthly = Array.isArray(growth?.monthly) ? growth.monthly : [];
    return {
        series: monthly.map((point: any) => ({
            label: pointLabel(point),
            added: Math.max(0, Number(point?.added) || 0),
            verified: Math.max(0, Number(point?.verified) || 0),
        })),
        totalAdded: Math.max(0, Number(growth?.totalAdded) || 0),
        totalVerified: Math.max(0, Number(growth?.totalVerified) || 0),
        verificationRate: Math.max(0, Number(growth?.verificationRate) || 0),
    };
}

/* ----------------------------- Helpers ----------------------------------- */

const fmtCompact = (n: number) =>
    n >= 1000 ? `${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}k` : `${n}`;
const fmtFull = (n: number) => n.toLocaleString();

/* --------------------------- Custom tooltip ------------------------------ */

function ChartTooltip({ active, payload, label }: any) {
    if (!active || !payload || !payload.length) return null;
    const added = payload.find((p: any) => p.dataKey === "added")?.value ?? 0;
    const verified = payload.find((p: any) => p.dataKey === "verified")?.value ?? 0;
    const rate = added > 0 ? Math.round((verified / added) * 100) : 0;
    return (
        <div className="qg-tip">
            <div className="qg-tip__label">{label}</div>
            <div className="qg-tip__row">
                <span className="qg-tip__key">
                    <span className="qg-tip__dot" style={{ background: COLOR_ADDED }} /> Added
                </span>
                <span className="qg-tip__val">{fmtFull(added)}</span>
            </div>
            <div className="qg-tip__row">
                <span className="qg-tip__key">
                    <span className="qg-tip__dot" style={{ background: COLOR_VERIFIED }} /> Verified
                </span>
                <span className="qg-tip__val">{fmtFull(verified)}</span>
            </div>
            <div className="qg-tip__rate">
                Verification rate <b>{rate}%</b>
            </div>
        </div>
    );
}

/* ------------------------------ Component -------------------------------- */

export default function QuestionGrowthChart({ data: growth, loading, range, onRangeChange }: QuestionGrowthChartProps) {
    const [hidden, setHidden] = useState<{ added: boolean; verified: boolean }>({ added: false, verified: false });

    // Chart series with a derived per-point verification rate (for the tooltip).
    const data = useMemo<ChartDatum[]>(() => {
        return (growth?.series ?? []).map((point) => ({
            ...point,
            rate: point.added > 0 ? point.verified / point.added : 0,
        }));
    }, [growth]);

    const totals = useMemo(() => {
        // Use the totals the API already computed for this period.
        const added = growth?.totalAdded ?? 0;
        const verified = growth?.totalVerified ?? 0;
        const rate = Math.round(growth?.verificationRate ?? 0);
        // Month-over-month growth on "added" between the last two points.
        let mom = 0;
        if (data.length >= 2) {
            const prev = data[data.length - 2].added;
            const curr = data[data.length - 1].added;
            mom = prev > 0 ? Math.round(((curr - prev) / prev) * 100) : 0;
        }
        return { added, verified, rate, mom };
    }, [growth, data]);

    const toggle = (key: "added" | "verified") =>
        setHidden((h) => ({ ...h, [key]: !h[key] }));

    const momUp = totals.mom >= 0;

    return (
        <div className="qg-card">
            {/* Header */}
            <div className="qg-head">
                <div>
                    <h3 className="qg-head__title">
                        <span className="qg-head__icon"><FontAwesomeIcon icon={faChartLine} /></span>
                        Question Library Growth
                    </h3>
                    <p className="qg-head__sub">Questions added and verified over time</p>
                </div>
                <div className="qg-head__right">
                    {!loading && (
                        <span className={`qg-badge ${momUp ? "qg-badge--up" : "qg-badge--down"}`}>
                            <FontAwesomeIcon icon={momUp ? faArrowTrendUp : faArrowTrendDown} />
                            {momUp ? "+" : ""}{totals.mom}% this period
                        </span>
                    )}
                    <div className="qg-seg" role="tablist" aria-label="Date range">
                        {RANGES.map((r) => (
                            <button
                                key={r.key}
                                type="button"
                                className={`qg-seg__btn ${range === r.key ? "is-active" : ""}`}
                                aria-pressed={range === r.key}
                                onClick={() => onRangeChange(r.key)}
                            >
                                {r.label.replace("Last ", "")}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Summary stats */}
            <div className="qg-stats">
                <div className="qg-stat">
                    <div className="qg-stat__label"><span className="qg-stat__dot" style={{ background: COLOR_ADDED }} />Total Added</div>
                    <div className="qg-stat__value">{loading ? "—" : fmtFull(totals.added)}</div>
                </div>
                <div className="qg-stat">
                    <div className="qg-stat__label"><span className="qg-stat__dot" style={{ background: COLOR_VERIFIED }} />Total Verified</div>
                    <div className="qg-stat__value">{loading ? "—" : fmtFull(totals.verified)}</div>
                </div>
                <div className="qg-stat">
                    <div className="qg-stat__label"><span className="qg-stat__dot" style={{ background: "#94A3B8" }} />Verification Rate</div>
                    <div className="qg-stat__value">{loading ? "—" : `${totals.rate}%`}</div>
                </div>
            </div>

            {/* Legend (toggles series) */}
            <div className="qg-legend">
                <span
                    className={`qg-legend__item ${hidden.added ? "is-off" : ""}`}
                    onClick={() => toggle("added")}
                >
                    <span className="qg-legend__swatch" style={{ background: COLOR_ADDED }} />
                    Questions Added
                </span>
                <span
                    className={`qg-legend__item ${hidden.verified ? "is-off" : ""}`}
                    onClick={() => toggle("verified")}
                >
                    <span className="qg-legend__swatch" style={{ background: COLOR_VERIFIED }} />
                    Questions Verified
                </span>
            </div>

            {/* Chart / skeleton */}
            {loading ? (
                <div className="qg-skeleton" style={{ height: 300, marginTop: 10 }} />
            ) : (
                <div className="qg-fade-in" style={{ width: "100%", height: 300, marginTop: 6 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={data} margin={{ top: 14, right: 8, left: -8, bottom: 0 }}>
                            <defs>
                                <linearGradient id="qgAdded" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor={COLOR_ADDED} stopOpacity={0.28} />
                                    <stop offset="100%" stopColor={COLOR_ADDED} stopOpacity={0} />
                                </linearGradient>
                                <linearGradient id="qgVerified" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor={COLOR_VERIFIED} stopOpacity={0.24} />
                                    <stop offset="100%" stopColor={COLOR_VERIFIED} stopOpacity={0} />
                                </linearGradient>
                            </defs>

                            <CartesianGrid strokeDasharray="3 6" vertical={false} stroke="#EEF1F6" />
                            <XAxis
                                dataKey="label"
                                tickLine={false}
                                axisLine={false}
                                tick={{ fontSize: 12, fill: "#94A3B8" }}
                                dy={10}
                                interval="preserveStartEnd"
                                minTickGap={18}
                            />
                            <YAxis
                                tickLine={false}
                                axisLine={false}
                                width={44}
                                tick={{ fontSize: 12, fill: "#94A3B8" }}
                                tickFormatter={fmtCompact}
                            />
                            <Tooltip
                                content={<ChartTooltip />}
                                cursor={{ stroke: "#CBD5E1", strokeWidth: 1.5, strokeDasharray: "4 4" }}
                            />

                            {!hidden.added && (
                                <Area
                                    type="monotone"
                                    dataKey="added"
                                    name="Questions Added"
                                    stroke={COLOR_ADDED}
                                    strokeWidth={3}
                                    fill="url(#qgAdded)"
                                    dot={false}
                                    activeDot={{ r: 5, strokeWidth: 2, stroke: "#fff", fill: COLOR_ADDED }}
                                    animationDuration={900}
                                    animationEasing="ease-out"
                                />
                            )}
                            {!hidden.verified && (
                                <Area
                                    type="monotone"
                                    dataKey="verified"
                                    name="Questions Verified"
                                    stroke={COLOR_VERIFIED}
                                    strokeWidth={3}
                                    fill="url(#qgVerified)"
                                    dot={false}
                                    activeDot={{ r: 5, strokeWidth: 2, stroke: "#fff", fill: COLOR_VERIFIED }}
                                    animationDuration={1100}
                                    animationEasing="ease-out"
                                />
                            )}
                        </ComposedChart>
                    </ResponsiveContainer>
                </div>
            )}
        </div>
    );
}
